<template>
  <div class="mt-2">
    <div class="flex flex-wrap justify-between items-end gap-3 mb-3">
      <div>
        <h2 class="text-lg font-semibold">Assessment Types</h2>
        <p class="text-xs text-gray-500 mt-0.5">
          Manage every assessment-type record for this school — reusable building blocks plus term- and subject-specific ones.
        </p>
      </div>
      <Button label="New Assessment Type" icon="pi pi-plus" size="small" @click="openCreate" />
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Subject</label>
        <SubjectSelect v-model="filterSubjectId" placeholder="All subjects" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Term</label>
        <AcademicCalendarSelect v-model="filterTermId" size="small" placeholder="All terms" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Type</label>
        <Select v-model="typeFilter" :options="typeFilterOptions" optionLabel="label" optionValue="value" class="w-full" />
      </div>
      <div class="flex items-end pb-1">
        <div class="flex items-center gap-2">
          <Checkbox v-model="includeReusable" :binary="true" inputId="include-reusable" />
          <label for="include-reusable" class="text-sm text-gray-700">Include reusable</label>
        </div>
      </div>
    </div>

    <p v-if="isFiltered" class="text-[11px] text-gray-500 mb-2 flex items-center gap-1">
      <i class="pi pi-info-circle"></i>
      Drag-to-reorder is disabled while a subject, term or category filter is active — clear filters to reorder the full list (sequence is a single school-wide order).
    </p>

    <DataTable
      :value="assessments"
      :loading="loading"
      dataKey="id"
      size="small"
      stripedRows
      paginator
      :rows="15"
      :reorderableRows="canReorder"
      tableStyle="min-width: 50rem"
      @rowReorder="onRowReorder"
    >
      <template #empty>
        <div class="text-center py-6 text-gray-500 italic">No assessment types match these filters.</div>
      </template>

      <Column v-if="canReorder" rowReorder :style="{ width: '3rem' }" />
      <Column field="name" header="Name" sortable />
      <Column header="Max" :style="{ width: '5rem' }">
        <template #body="{ data }">
          <span class="font-semibold">{{ data.max_score }}</span>
        </template>
      </Column>
      <Column header="Category" :style="{ width: '7rem' }">
        <template #body="{ data }">
          <Tag v-if="data.is_ca" value="CA" severity="info" />
          <Tag v-else-if="data.is_exam" value="Exam" severity="warn" />
          <span v-else class="text-gray-400">—</span>
        </template>
      </Column>
      <Column header="Scope" :style="{ width: '7rem' }">
        <template #body="{ data }">
          <Tag :value="scopeOf(data).label" :severity="scopeOf(data).severity" />
        </template>
      </Column>
      <Column header="Subject">
        <template #body="{ data }">{{ data.subject?.name ?? '—' }}</template>
      </Column>
      <Column header="Term">
        <template #body="{ data }">{{ termLabel(data) }}</template>
      </Column>
      <Column header="Actions" :style="{ width: '7rem' }">
        <template #body="{ data }">
          <div class="flex gap-1">
            <Button icon="pi pi-pencil" text rounded severity="secondary" size="small"
              @click="openEdit(data)" v-tooltip.top="'Edit'" />
            <Button icon="pi pi-trash" text rounded severity="danger" size="small"
              :loading="deletingId === data.id"
              @click="confirmDeleteAssessment(data)" v-tooltip.top="'Delete'" />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Create / Edit dialog -->
    <Dialog v-model:visible="dialogVisible" modal :header="editingId ? 'Edit Assessment Type' : 'New Assessment Type'" :style="{ width: '480px' }">
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <InputText v-model="form.name" class="w-full" placeholder="e.g., Mid-Term Test" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Max Score</label>
          <InputNumber v-model="form.max_score" class="w-full" :min="1" :max="1000" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Description <span class="text-gray-400">(optional)</span></label>
          <Textarea v-model="form.description" class="w-full" rows="2" autoResize />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <Select v-model="form.category" :options="categoryOptions" optionLabel="label" optionValue="value" class="w-full" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Subject <span class="text-gray-400">(optional)</span></label>
            <SubjectSelect v-model="form.subject_id" placeholder="None" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Term <span class="text-gray-400">(optional)</span></label>
            <AcademicCalendarSelect v-model="form.academic_calendar_id" size="small" placeholder="None" />
          </div>
        </div>
        <p class="text-xs" :class="isReusableScope ? 'text-primary' : 'text-orange-600'">
          <template v-if="isReusableScope">
            Reusable: no subject or term selected, so this becomes a school-wide building block usable in any result template.
          </template>
          <template v-else>
            Scoped: this assessment type will only apply to the selected {{ scopeWords }}.
          </template>
        </p>
      </div>

      <template #footer>
        <Button label="Cancel" text @click="dialogVisible = false" />
        <Button :label="editingId ? 'Save' : 'Create'" :loading="creating || updating" @click="submitForm" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import SubjectSelect from '@/components/common/SubjectSelect.vue';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import type { DataTableRowReorderEvent } from 'primevue/datatable';
import {
  useSchoolAssessmentTypes,
  type AssessmentTypeFilters,
  type ManagedAssessmentType,
} from '@/composables/useSchoolAssessmentTypes';

type TypeFilter = 'ALL' | 'CA' | 'EXAM';
type Category = 'NONE' | 'CA' | 'EXAM';

// `string | undefined` (not null) to match the select components' modelValue type.
const filterSubjectId = ref<string>();
const filterTermId = ref<string>();
const typeFilter = ref<TypeFilter>('ALL');
const includeReusable = ref(true);

const typeFilterOptions = [
  { label: 'All categories', value: 'ALL' },
  { label: 'Continuous Assessment', value: 'CA' },
  { label: 'Examination', value: 'EXAM' },
];

const categoryOptions = [
  { label: 'Uncategorised', value: 'NONE' },
  { label: 'Continuous Assessment (CA)', value: 'CA' },
  { label: 'Examination', value: 'EXAM' },
];

const filters = computed<AssessmentTypeFilters>(() => ({
  subject_id: filterSubjectId.value || null,
  academic_calendar_id: filterTermId.value || null,
  is_ca: typeFilter.value === 'CA' ? true : undefined,
  is_exam: typeFilter.value === 'EXAM' ? true : undefined,
  include_reusable: includeReusable.value,
}));

// `sequence` is a single flat per-school order (the backend reorder writes
// `sequence = index + 1` over whatever ids it receives, unscoped). So drag-reorder
// is only safe on the COMPLETE list — when no narrowing filter is active the query
// returns every row ordered by sequence, making a drag a clean full renumber. With
// any subject/term/category filter the view is a partial subset, so renumbering it
// would clobber the hidden rows' order. Gate reorder on an unfiltered view.
const isFiltered = computed(() => !!filterSubjectId.value || !!filterTermId.value || typeFilter.value !== 'ALL');
const canReorder = computed(() => !isFiltered.value);

const enabled = ref(true);

const {
  assessments,
  loading,
  creating,
  updating,
  deletingId,
  createAssessment,
  updateAssessment,
  confirmDeleteAssessment,
  reorder,
} = useSchoolAssessmentTypes(filters, enabled);

// --- Display helpers ---
type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

function scopeOf(a: ManagedAssessmentType): { label: string; severity: TagSeverity } {
  if (!a.subject_id && !a.academic_calendar_id) return { label: 'Reusable', severity: 'success' };
  if (a.subject_id) return { label: 'Subject', severity: 'contrast' };
  return { label: 'Term', severity: 'info' };
}

function termLabel(a: ManagedAssessmentType): string {
  if (!a.academicCalendar) return '—';
  const session = a.academicCalendar.session?.name ?? '';
  const term = a.academicCalendar.termStructure?.name ?? '';
  return [session, term].filter(Boolean).join(' - ') || '—';
}

function onRowReorder(event: DataTableRowReorderEvent) {
  reorder(event.value as ManagedAssessmentType[]);
}

// --- Create / Edit dialog ---
const dialogVisible = ref(false);
const editingId = ref<string | null>(null);
const form = reactive<{
  name: string;
  max_score: number;
  description: string | null;
  category: Category;
  subject_id: string | undefined;
  academic_calendar_id: string | undefined;
}>({
  name: '',
  max_score: 100,
  description: null,
  category: 'NONE',
  subject_id: undefined,
  academic_calendar_id: undefined,
});

const isReusableScope = computed(() => !form.subject_id && !form.academic_calendar_id);
const scopeWords = computed(() => {
  const parts: string[] = [];
  if (form.subject_id) parts.push('subject');
  if (form.academic_calendar_id) parts.push('term');
  return parts.join(' and ');
});

function resetForm() {
  form.name = '';
  form.max_score = 100;
  form.description = null;
  form.category = 'NONE';
  form.subject_id = undefined;
  form.academic_calendar_id = undefined;
}

function openCreate() {
  editingId.value = null;
  resetForm();
  dialogVisible.value = true;
}

function openEdit(item: ManagedAssessmentType) {
  editingId.value = item.id;
  form.name = item.name;
  form.max_score = item.max_score;
  form.description = item.description;
  form.category = item.is_ca ? 'CA' : item.is_exam ? 'EXAM' : 'NONE';
  form.subject_id = item.subject_id ?? undefined;
  form.academic_calendar_id = item.academic_calendar_id ?? undefined;
  dialogVisible.value = true;
}

async function submitForm() {
  const values = {
    name: form.name,
    max_score: form.max_score,
    description: form.description,
    is_ca: form.category === 'CA',
    is_exam: form.category === 'EXAM',
    subject_id: form.subject_id ?? null,
    academic_calendar_id: form.academic_calendar_id ?? null,
  };
  const ok = editingId.value
    ? await updateAssessment(editingId.value, values)
    : await createAssessment(values);
  if (ok) dialogVisible.value = false;
}
</script>
